'use client';

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { PulseButton } from 'src/components/PulseUI';

interface NavCalloutProps {
  triggerRef: React.RefObject<HTMLDivElement | null>;
  title: string;
  description: string;
  buttonLabel?: string;
  onDismiss: () => void;
  offsetY?: number;
}

const NavCallout = ({
  triggerRef,
  title,
  description,
  buttonLabel = 'Got it',
  onDismiss,
  offsetY = -5,
}: NavCalloutProps) => {
  const calloutRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!triggerRef.current) return;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition({
        top: rect.top + rect.height / 2 + offsetY,
        left: rect.right + 12,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [triggerRef, offsetY]);

  return createPortal(
    <div
      ref={calloutRef}
      role="status"
      aria-live="polite"
      data-testid="nav-callout"
      className="fixed z-[9999] w-80 px-4 py-3 bg-neutral-800 text-white rounded-xl
        shadow-lg -translate-y-1/2 flex items-center gap-3"
      style={{ top: position.top, left: position.left }}
    >
      {/* Arrow pointing left toward the trigger */}
      <div
        aria-hidden="true"
        className="absolute top-1/2 -left-1.5 translate-y-0.5 w-3 h-3 bg-neutral-800 rotate-45"
      />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold leading-5">{title}</p>
        <p className="text-sm text-neutral-300 leading-5">{description}</p>
      </div>
      <PulseButton
        size="sm"
        data-testid="nav-callout-dismiss"
        className="shrink-0 bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold
          rounded-full px-4"
        onClick={onDismiss}
      >
        {buttonLabel}
      </PulseButton>
      <button
        type="button"
        aria-label="Dismiss"
        data-testid="nav-callout-close"
        className="shrink-0 text-white hover:text-neutral-300 cursor-pointer"
        onClick={onDismiss}
      >
        <Icon icon={faXmark} className="w-4 h-4" />
      </button>
    </div>,
    document.body
  );
};

export default NavCallout;
